/**
 * structure_solids — a lightweight, physics-free spatial registry of the SOLID
 * volumes of streamed settlement buildings.
 *
 * Ship-flight collision and cannon-bolt hit-tests need to ask "is (x,z,y) inside
 * a building, and where is its surface?" at ANY altitude. The physics world is
 * unusable for this in flight: it only steps inside PhysicsModule.moveAvatar(),
 * which is parked while the avatar rides a ship, so colliders streamed during
 * flight never become query-visible. And the plan does not carry a usable roof
 * height (the renderer derives it from civTargetHeight + a capped fit-scale).
 *
 * So CommonsSettlementsModule registers each colliding building here at build
 * time from its ACTUAL world transform + scaled bounding box (base → roof), and
 * clears the site's solids on dispose. Buildings are boxes rotated about Y; the
 * registry bins them on a coarse grid so a query stays O(a few) even with several
 * cities resident. Everything lives in the same local/scene space the flight loop
 * and bolts use (site.centerX ↔ atlas.localX), so no transform is needed.
 */

export interface StructureSolid {
  /** Footprint center, world (== local/scene) space. */
  x: number;
  z: number;
  /** Footprint half-extents along the building's own X/Z (pre-yaw). */
  hx: number;
  hz: number;
  /** Footprint rotation about Y. */
  yaw: number;
  /** World Y span: ground (base) → roof (top). */
  base: number;
  top: number;
}

const BIN = 128; // m — a building footprint (≤ ~60 m half-extent) touches 1–2 cells
const bins = new Map<string, StructureSolid[]>();
const bySite = new Map<number, StructureSolid[]>();

function cellKeysFor(s: StructureSolid): string[] {
  // Conservative (yaw-agnostic) reach: the rotated rect never exceeds this box.
  const r = Math.max(s.hx, s.hz) + 1;
  const x0 = Math.floor((s.x - r) / BIN), x1 = Math.floor((s.x + r) / BIN);
  const z0 = Math.floor((s.z - r) / BIN), z1 = Math.floor((s.z + r) / BIN);
  const keys: string[] = [];
  for (let cx = x0; cx <= x1; cx++) for (let cz = z0; cz <= z1; cz++) keys.push(`${cx},${cz}`);
  return keys;
}

/** Every solid whose footprint reach touches a circle of `r` around (x, z) — rooftop perches for the birds (2026-09-11). */
export function structureSolidsNear(x: number, z: number, r: number): StructureSolid[] {
  const out: StructureSolid[] = [];
  const seen = new Set<StructureSolid>();
  const x0 = Math.floor((x - r) / BIN), x1 = Math.floor((x + r) / BIN);
  const z0 = Math.floor((z - r) / BIN), z1 = Math.floor((z + r) / BIN);
  for (let cx = x0; cx <= x1; cx++) for (let cz = z0; cz <= z1; cz++) {
    const arr = bins.get(`${cx},${cz}`);
    if (!arr) continue;
    for (const s of arr) {
      if (seen.has(s)) continue;
      seen.add(s);
      if (Math.hypot(s.x - x, s.z - z) <= r + Math.max(s.hx, s.hz)) out.push(s);
    }
  }
  return out;
}

/** Register one building's solid volume under a settlement id. */
export function addStructureSolid(siteId: number, s: StructureSolid): void {
  for (const k of cellKeysFor(s)) {
    let arr = bins.get(k);
    if (!arr) { arr = []; bins.set(k, arr); }
    arr.push(s);
  }
  let site = bySite.get(siteId);
  if (!site) { site = []; bySite.set(siteId, site); }
  site.push(s);
}

/** Drop every solid a settlement registered (called on disposeSettlement). */
export function clearStructureSolids(siteId: number): void {
  const arr = bySite.get(siteId);
  if (!arr) return;
  bySite.delete(siteId);
  const dead = new Set(arr);
  const touched = new Set<string>();
  for (const s of arr) for (const k of cellKeysFor(s)) touched.add(k);
  for (const k of touched) {
    const b = bins.get(k);
    if (!b) continue;
    const kept = b.filter((s) => !dead.has(s));
    if (kept.length) bins.set(k, kept); else bins.delete(k);
  }
}

/** Transform a world XZ into a solid's local (yaw-aligned) footprint frame.
 *  THE YAW IS THREE'S rotation about Y (every producer composes its instance with it:
 *  local x → (cos yaw, −sin yaw), local z → (sin yaw, cos yaw)). The first version
 *  inverted a standard 2D rotation instead — a MIRRORED frame that put every diagonal
 *  footprint's hit box at twice its angle (2026-09-16, found by the circuit barriers;
 *  axis-aligned boxes never showed it). */
function toLocal(s: StructureSolid, wx: number, wz: number): { lx: number; lz: number } {
  const dx = wx - s.x, dz = wz - s.z;
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { lx: dx * c - dz * sn, lz: dx * sn + dz * c };
}

/**
 * The tallest building solid that contains (wx, wz, y), or null. `margin` grows
 * the footprint so a ship (or bolt) registers a hit slightly before visual
 * overlap. Vertical span is [base, top]: a point above the roof is NOT a hit
 * (you may fly over), a point below the base is underground (terrain owns it).
 */
export function structureHitAt(wx: number, wz: number, y: number, margin = 0): StructureSolid | null {
  const arr = bins.get(`${Math.floor(wx / BIN)},${Math.floor(wz / BIN)}`);
  if (!arr) return null;
  let best: StructureSolid | null = null;
  for (const s of arr) {
    if (y < s.base || y > s.top + margin) continue;
    const { lx, lz } = toLocal(s, wx, wz);
    if (Math.abs(lx) <= s.hx + margin && Math.abs(lz) <= s.hz + margin) {
      if (!best || s.top > best.top) best = s;
    }
  }
  return best;
}

/**
 * Eject a point to just outside a solid's footprint along the axis of LEAST
 * penetration (the nearest face) — the flight collision push-out. Returns the
 * new world XZ. If the point is dead-center (no penetration axis), it is pushed
 * along +local-X as a degenerate fallback.
 */
export function structurePushOut(s: StructureSolid, wx: number, wz: number, margin = 4): { x: number; z: number } {
  let { lx, lz } = toLocal(s, wx, wz);
  const penX = s.hx + margin - Math.abs(lx);
  const penZ = s.hz + margin - Math.abs(lz);
  if (penX <= penZ) lx = (lx >= 0 ? 1 : -1) * (s.hx + margin);
  else lz = (lz >= 0 ? 1 : -1) * (s.hz + margin);
  // local → world (three's rotation about Y, then translate).
  const c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
  return { x: s.x + lx * c + lz * sn, z: s.z - lx * sn + lz * c };
}

/** Diagnostic: total registered solids across all resident settlements. */
export function structureSolidCount(): number {
  let n = 0;
  for (const arr of bySite.values()) n += arr.length;
  return n;
}

/** Test-only: wipe the registry (harness fixtures). */
export function __resetStructureSolids(): void {
  bins.clear();
  bySite.clear();
}
